import { useGetMasterCoursesQuery } from "../../store/api/masterCourseApi";

/**
 * Courses that continue into another course.
 *
 * The 2026-07-24 split turned the six-level "Leading with AI" into two standalone
 * three-level courses. Finishing Core is a genuine course completion and issues
 * its own certificate. That certificate becomes a Passport stamp only when its
 * Pedagogue-certified state was captured as true at issue time. For a learner
 * who also has the Advanced course, "you're done" is the wrong ending.
 *
 * Deliberately a small explicit map rather than a backend field: this is the only
 * pair that exists, and adding a `follow_on_course_id` column plus DTO plumbing
 * for one pair would be more moving parts than the problem is worth. Revisit if a
 * second pair appears.
 */
const FOLLOW_ON_COURSE: Record<string, string> = {
  // Leading with AI (Core, levels 1-3) -> Leading with AI: Advanced (levels 4-6)
  "d936df36-b9c5-4188-8929-d85b5c0abfa2": "5b2abf33-7153-4a66-9680-c3b0e3de9bec",
};

export interface FollowOnCourse {
  id: string;
  title: string;
}

/**
 * The course to offer next after completing `masterCourseId`, or undefined.
 *
 * Returns a course ONLY when it appears in the learner's own accessible-courses
 * response. That list is already access-filtered by the backend, so learners
 * whose package omits Advanced get `undefined` instead of a CTA that would
 * 403.
 *
 * `enabled` is false everywhere except the course-completion screen, so this
 * costs one extra request at most once per course completion.
 */
export function useFollowOnCourse(
  masterCourseId: string | undefined,
  enabled: boolean,
): FollowOnCourse | undefined {
  const followOnId = masterCourseId ? FOLLOW_ON_COURSE[masterCourseId] : undefined;
  const { data: courses } = useGetMasterCoursesQuery(undefined, {
    skip: !enabled || !followOnId,
  });

  if (!followOnId || !courses) {return undefined;}
  const match = courses.find((c) => c.id === followOnId);
  if (!match) {return undefined;}
  return { id: match.id, title: match.title };
}
